// Offline export of a conversation's diagnostics. Two shapes, because the two uses are different:
// the JSON keeps full fidelity so the data can be re-loaded or diffed, while the text log is what
// you grep, paste into a bug, or hand to someone without tooling.
import type {
  ConversationDiagnosticsResponse,
  LogEntry,
  TelemetryNode,
} from './diagnosticsClient';
import { severityLabel } from './diagnosticsClient';

/** Triggers a browser download for a generated file, then releases the object URL. */
export function downloadTextFile(filename: string, contents: string, mime: string): void {
  downloadBlob(filename, new Blob([contents], { type: `${mime};charset=utf-8` }));
}

/** Triggers a browser download for binary content (e.g. a zip), then releases the object URL. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    // Firefox requires the anchor to be in the document for a programmatic click to take.
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoking synchronously can cancel the download in some browsers; defer past the click.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/** Safe, sortable filename stem: `conversation-<id>-<utc timestamp>`. */
function fileStem(conversationId: string): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').replace('Z', '');
  const safeId = conversationId.replace(/[^a-zA-Z0-9._-]/g, '_');
  return `conversation-${safeId}-${stamp}`;
}

/**
 * The complete response, verbatim and pretty-printed.
 *
 * Deliberately the raw payload rather than a reshaped "report": whatever the page could show, the
 * file contains — including fields the UI happens not to render today, which is exactly what you
 * want when the reason you are downloading is that the UI did not show you enough.
 */
export function downloadTelemetryJson(
  conversationId: string,
  result: ConversationDiagnosticsResponse,
): void {
  downloadTextFile(
    `${fileStem(conversationId)}.json`,
    JSON.stringify(result, null, 2),
    'application/json',
  );
}

function pad(value: string | null | undefined, width: number): string {
  const text = value ?? '';
  return text.length >= width ? text : text + ' '.repeat(width - text.length);
}

function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '';
  if (ms < 1) return `${Math.round(ms * 1000)}us`;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/** Indented span tree, so the text log keeps the shape the Transaction tab shows. */
function renderTree(node: TelemetryNode, depth: number, out: string[]): void {
  const indent = '  '.repeat(depth);
  const bits = [
    `${indent}${node.name ?? '(unnamed)'}`,
    node.category ? `[${node.category}]` : '',
    node.target ? `target=${node.target}` : '',
    node.resultCode ? `result=${node.resultCode}` : '',
    node.durationMs !== null && node.durationMs !== undefined ? `duration=${formatMs(node.durationMs)}` : '',
  ].filter(Boolean);
  out.push(bits.join('  '));

  for (const ex of node.exceptions) {
    out.push(`${indent}  !! EXCEPTION ${ex.name ?? ''}`);
    if (ex.method) out.push(`${indent}     failed method: ${ex.method}`);
    if (ex.detail) out.push(`${indent}     ${ex.detail}`);
    if (ex.callStack) {
      for (const line of ex.callStack.split('\n')) out.push(`${indent}     ${line}`);
    }
  }

  for (const child of node.children) renderTree(child, depth + 1, out);
}

/**
 * Human-readable log: a header, the span tree, then the full chronological log stream.
 *
 * Nothing is truncated or elided — the point of an offline copy is that it holds everything the
 * screen could not.
 */
export function buildTelemetryLogText(
  conversationId: string,
  result: ConversationDiagnosticsResponse,
  logs: LogEntry[],
): string {
  const { telemetry, source } = result;
  const out: string[] = [];

  out.push('='.repeat(100));
  out.push(`Conversation Diagnostics — ${conversationId}`);
  out.push('='.repeat(100));
  out.push(`Exported          : ${new Date().toISOString()}`);
  out.push(`Source            : ${source === 'live' ? 'live query' : 'archived snapshot'}`);
  out.push(`Generated at      : ${telemetry.generatedAt}`);
  out.push(`Complete          : ${telemetry.complete}`);
  out.push(`Operations        : ${telemetry.operations.length}`);
  out.push(`Exceptions        : ${telemetry.exceptions.length}`);
  out.push(`Log entries       : ${logs.length}`);
  if (!telemetry.complete) {
    out.push('');
    out.push('NOTE: telemetry was still landing when this was captured — App Insights ingestion');
    out.push('      lags by several minutes, so operations may be missing.');
  }
  out.push('');

  out.push('-'.repeat(100));
  out.push('TRANSACTION');
  out.push('-'.repeat(100));
  for (const op of telemetry.operations) {
    out.push('');
    out.push(`OPERATION ${op.operationId}`);
    out.push(`  name     : ${op.name ?? ''}`);
    out.push(`  start    : ${op.startTime ?? ''}`);
    out.push(`  duration : ${formatMs(op.durationMs)}`);
    out.push(`  roots    : ${(op.roots ?? []).length}`);
    out.push('');
    for (const root of op.roots ?? []) renderTree(root, 1, out);
  }

  out.push('');
  out.push('-'.repeat(100));
  out.push(`LOGS & TRACES (${logs.length}, chronological)`);
  out.push('-'.repeat(100));
  for (const { log, spanName, isException } of logs) {
    const time = log.timestamp ? new Date(log.timestamp).toISOString() : '';
    const level = isException ? 'EXCEPTION' : severityLabel(log.severityLevel).toUpperCase();
    const message = isException ? (log.detail ?? log.name ?? '') : (log.name ?? '');
    out.push(`${pad(time, 26)} ${pad(level, 11)} ${pad(spanName, 38)} ${message}`);

    // An exception's stack is the reason the file exists; keep it with its entry.
    if (isException && log.callStack) {
      for (const line of log.callStack.split('\n')) out.push(`${' '.repeat(26)} ${line}`);
    }
  }

  out.push('');
  return out.join('\n');
}

export function downloadTelemetryLog(
  conversationId: string,
  result: ConversationDiagnosticsResponse,
  logs: LogEntry[],
): void {
  downloadTextFile(
    `${fileStem(conversationId)}.log`,
    buildTelemetryLogText(conversationId, result, logs),
    'text/plain',
  );
}
